import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { getDocTypes, pollDocumentGeneration } from "../api";
import { formatEstimate } from "./DraftStep";
import SkillGraph from "./SkillGraph";
import { AlertIcon, ArrowLeftIcon, CheckIcon, ClockIcon, DownloadIcon, FileNotesIcon, FileTextIcon, SpinnerIcon, } from "./Icons";
function StepMark({ status }) {
    if (status === "done")
        return _jsx(CheckIcon, { size: 13 });
    if (status === "running")
        return _jsx(SpinnerIcon, { size: 14 });
    if (status === "error")
        return _jsx(AlertIcon, { size: 14 });
    if (status === "skipped")
        return _jsx("span", { children: "\u2013" });
    return null;
}
function downloadBase64Docx(base64, filename) {
    const bytes = atob(base64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++)
        arr[i] = bytes.charCodeAt(i);
    const blob = new Blob([arr], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}
export default function GenerationView({ runId, docTypeLabel, documentCount, onStartOver }) {
    const [status, setStatus] = useState(null);
    const [docTypes, setDocTypes] = useState([]);
    const [elapsed, setElapsed] = useState(0);
    const startRef = useRef(Date.now());
    useEffect(() => {
        getDocTypes()
            .then((r) => setDocTypes(r.documentTypes))
            .catch(() => undefined);
    }, []);
    useEffect(() => {
        let stopped = false;
        const poll = async () => {
            try {
                const s = await pollDocumentGeneration(runId);
                if (stopped)
                    return;
                setStatus(s);
                if (s.status === "running")
                    setTimeout(poll, 2500);
            }
            catch {
                if (!stopped)
                    setTimeout(poll, 4000);
            }
        };
        poll();
        const timer = setInterval(() => setElapsed(Math.round((Date.now() - startRef.current) / 1000)), 1000);
        return () => {
            stopped = true;
            clearInterval(timer);
        };
    }, [runId]);
    const done = (status === null || status === void 0 ? void 0 : status.status) === "done";
    const failed = (status === null || status === void 0 ? void 0 : status.status) === "error";
    const result = (status === null || status === void 0 ? void 0 : status.result) || null;
    const currentDocType = docTypes.find((d) => { var _a; return d.id === ((_a = status === null || status === void 0 ? void 0 : status.docType) === null || _a === void 0 ? void 0 : _a.id); }) || null;
    const mmss = `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;
    return (_jsxs("div", { className: "card", children: [_jsx("h2", { children: done ? docTypeLabel : `Drafting your ${docTypeLabel}…` }), _jsx("p", { className: "sub", children: done
                    ? "Your draft and working notes are ready below."
                    : failed
                        ? "The run hit an error."
                        : "Suade is running each skill in sequence and grounding it in your matter." }), status && (_jsxs("div", { className: "banner info", style: { marginBottom: 16 }, children: [_jsx(ClockIcon, { size: 16 }), _jsx("span", { children: done
                            ? `Completed in ${mmss}`
                            : `Elapsed ${mmss} · estimated ${formatEstimate(status.estimateSeconds)}` })] })), currentDocType && (_jsx(SkillGraph, { docType: currentDocType, steps: status === null || status === void 0 ? void 0 : status.steps, documentCount: documentCount })), _jsx("div", { className: "pipeline", children: ((status === null || status === void 0 ? void 0 : status.steps) || []).map((s) => (_jsxs("div", { className: `pipe-step ${s.status}`, children: [_jsx("span", { className: "pipe-dot", children: _jsx(StepMark, { status: s.status }) }), _jsx("span", { className: "pipe-label", children: s.label }), _jsx("span", { className: "pipe-meta", children: s.status === "running"
                                ? "drafting…"
                                : s.status === "done" && s.seconds
                                    ? `${s.seconds}s`
                                    : s.status === "skipped"
                                        ? "no skill yet"
                                        : s.status === "error"
                                            ? s.error || "error"
                                            : "" })] }, s.skillId))) }), failed && (_jsxs("div", { className: "error-banner", children: [_jsx(AlertIcon, { size: 16 }), _jsx("span", { children: status === null || status === void 0 ? void 0 : status.error })] })), done && result && (_jsxs("div", { className: "downloads", children: [_jsxs("div", { className: "dl-card", children: [_jsx("span", { className: "dl-icon", children: _jsx(FileTextIcon, { size: 20 }) }), _jsxs("div", { className: "dl-meta", children: [_jsxs("div", { className: "dl-title", children: ["Draft \u2014 ", result.docTypeLabel] }), _jsx("div", { className: "dl-sub", children: result.draftFilename || "Not produced" })] }), _jsxs("button", { className: "btn primary", disabled: !result.draftDocxBase64 || !result.draftFilename, onClick: () => downloadBase64Docx(result.draftDocxBase64, result.draftFilename), children: [_jsx(DownloadIcon, { size: 16 }), "Download"] })] }), _jsxs("div", { className: "dl-card", children: [_jsx("span", { className: "dl-icon", children: _jsx(FileNotesIcon, { size: 20 }) }), _jsxs("div", { className: "dl-meta", children: [_jsx("div", { className: "dl-title", children: "Working notes" }), _jsx("div", { className: "dl-sub", children: result.workingNotesFilename || "Not produced" })] }), _jsxs("button", { className: "btn secondary", disabled: !result.workingNotesDocxBase64 || !result.workingNotesFilename, onClick: () => downloadBase64Docx(result.workingNotesDocxBase64, result.workingNotesFilename), children: [_jsx(DownloadIcon, { size: 16 }), "Download"] })] })] })), _jsxs("div", { className: "btn-row", children: [_jsxs("button", { className: "btn secondary", onClick: onStartOver, children: [_jsx(ArrowLeftIcon, { size: 16 }), "Start a new draft"] }), _jsx("span", {})] })] }));
}
